import { BrowserRouter as Router, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react"
import NavBar from "./components/NavBar";
import "@fontsource/lora";
import "@fontsource/anton";

// Lazy load all section components
const Books = lazy(() => import("./components/Books"));
const Playlists = lazy(() => import("./components/Playlists"));
const Elsewhere = lazy(() => import("./components/Elsewhere"));
const Albums = lazy(() => import("./components/Albums"));
const About = lazy(() => import("./components/About"));
const Places = lazy(() => import("./components/Places"));
const Upload = lazy(() => import("./components/Upload"));

// Preload functions - calling these warms the module cache
export const preloadFunctions = {
  albums: () => import("./components/Albums"),
  books: () => import("./components/Books"),
  playlists: () => import("./components/Playlists"),
  elsewhere: () => import("./components/Elsewhere"),
  about: () => import("./components/About"),
  places: () => import("./components/Places"),
};

// Valid section paths
const SECTION_PATHS = ['/albums', '/books', '/places', '/playlists', '/elsewhere', '/about'];

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center py-20">
    <div className="animate-pulse text-gray-500">Loading...</div>
  </div>
);

// Background color mapping based on route
const getBackgroundColor = (pathname: string): string => {
  switch (pathname) {
    case "/books":
      return "bg-stone-300";
    case "/playlists":
      return "bg-emerald-500";
    case "/elsewhere":
      return "bg-amber-300";
    case "/albums":
      return "bg-white";
    case "/about":
      return "bg-white";
    case "/places":
      return "bg-slate-300";
    default:
      return "bg-white";
  }
};

// Main content with all sections always mounted but visibility controlled by route
const MainContent: React.FC = () => {
  const location = useLocation();
  const backgroundColor = getBackgroundColor(location.pathname);
  const currentPath = location.pathname;

  // Preload all sections after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      Object.values(preloadFunctions).forEach(preloadFn => {
        preloadFn();
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`min-h-screen text-gray-900 p-4 md:p-8 md:px-40 ${backgroundColor}`}>
      {/* Application title */}
      <h1 className="text-4xl md:text-5xl font-normal mb-4 md:mb-6 font-title">
        CJF's Music Worldview
      </h1>
      <hr className="border-gray-600 mb-6 md:mb-8" />
      {/* Navigation bar */}
      <NavBar />

      {/* All sections stay mounted, visibility controlled by route */}
      <Suspense fallback={<LoadingFallback />}>
        <div className={`transition-opacity duration-300 ${currentPath === "/albums" ? "opacity-100" : "opacity-0 hidden"}`}>
          <Albums />
        </div>
        <div className={`transition-opacity duration-300 ${currentPath === "/books" ? "opacity-100" : "opacity-0 hidden"}`}>
          <Books />
        </div>
        <div className={`transition-opacity duration-300 ${currentPath === "/places" ? "opacity-100" : "opacity-0 hidden"}`}>
          <Places />
        </div>
        <div className={`transition-opacity duration-300 ${currentPath === "/playlists" ? "opacity-100" : "opacity-0 hidden"}`}>
          <Playlists />
        </div>
        <div className={`transition-opacity duration-300 ${currentPath === "/elsewhere" ? "opacity-100" : "opacity-0 hidden"}`}>
          <Elsewhere />
        </div>
        <div className={`transition-opacity duration-300 ${currentPath === "/about" ? "opacity-100" : "opacity-0 hidden"}`}>
          <About />
        </div>
      </Suspense>
    </div>
  );
};

// Router content - decides between MainContent and Upload
const RouterContent: React.FC = () => {
  const location = useLocation();

  // Handle redirects for root and invalid paths
  if (location.pathname === '/') {
    return <Navigate to="/albums" replace />;
  }

  // Upload has its own layout
  if (location.pathname === '/upload') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Upload />
      </Suspense>
    );
  }

  // Redirect invalid paths to albums
  if (!SECTION_PATHS.includes(location.pathname)) {
    return <Navigate to="/albums" replace />;
  }

  // Main sections - this component stays mounted across section changes
  return <MainContent />;
};

function App() {
  return (
    <Router>
      <RouterContent />
      <Analytics />
    </Router>
  );
}

export default App;
